import { computed, effect, inject, Injectable, NgZone } from '@angular/core'
import { NavigationStart, Router } from '@angular/router'
import {
  _isNotNullish,
  _lastOrUndefined,
  AdminService,
  IsoDateTime,
  localDate,
  localTime,
  StringMap,
} from '@naturalcycles/js-lib'
import {
  AgeRangeOption,
  AvgCycleLengthOption,
  BestTimeInCycleOption,
  BirthControlMethodBreakpointOption,
  ConceiveTimingBreakpointOption,
  ConsideringPurchaseOption,
  cookieService,
  CycleChangesBreakpointOption,
  DeviceTraitOption,
  DiscussedFertilityOption,
  FeelingStressedBreakpointOption,
  GoalBreakpointOption,
  HormonalBirthControlHowLongBreakpointOption,
  HormonalBirthControlUsageBreakpointOption,
  KnowingYourSexDriveOption,
  KnowPregnantDaysOption,
  MedicalConditionsBreakpointOption,
  NightSleepBreakpointOption,
  PartnerInvolvementOption,
  PeriodRegularityOption,
  QuizDataInput,
  SexFrequencyOption,
  shortQuizGoalOptions,
  SideEffectsBreakpointOption,
  SleepConsistencyOption,
  SleepOption,
  SleepWithTrackerOption,
  StatementDescriptionOption,
  TrueOrFalseOption,
  WearWatchToBedOption,
  WhichWearableBreakpointOption,
} from '@naturalcycles/shared'
import { TranslateService } from '@ngx-translate/core'
import {
  AdminMenuAction,
  AdminMenuModalComponent,
} from '@src/app/components/modals/admin-menu/admin-menu-modal.component.js'
import { goalBreakpointOptionToGoal, quizParentSlug } from '@src/app/constants/quiz.js'
import { QuizService } from '@src/app/core/services/quiz/quiz.service.js'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { Page, ParentPage } from '@src/app/shared/typings/enum/pages.js'
import { Breakpoint } from '@src/app/shared/typings/enum/quiz.js'
import { FlowDefinition } from '@src/app/shared/typings/interfaces/quiz.js'
import { Subscription } from 'rxjs'
import timekeeper from 'timekeeper'
import { getMatchingTranslationKey, recInspect } from '../util/inspector.util.js'
import { logger } from '../util/log.util.js'
import { getFlowForDefinition } from '../util/quiz.util.js'
import { ModalService } from './modal.service.js'
import { OrderService } from './order.service.js'
import { SessionService } from './session.service.js'

interface QuizDataInputWithABTests extends QuizDataInput {}

@Injectable({ providedIn: 'root' })
export class QaService {
  private store = inject(SignalStore)
  private modalService = inject(ModalService)
  private router = inject(Router)
  private quizService = inject(QuizService)
  private orderService = inject(OrderService)
  private translateService = inject(TranslateService)
  private ngZone = inject(NgZone)
  private sessionService = inject(SessionService)
  private reddot = computed(() => this.store.$reddot())

  constructor() {
    this.adminService = new AdminService({
      onChange: adminMode => logger.log({ adminMode }),
      onRedDotClick: () => this.displayMenu(),
    })
    effect(() => {
      const overrideTime = this.reddot()?.overrideTime
      if (overrideTime) {
        timekeeper.travel(localTime(overrideTime).toDate())
      } else {
        timekeeper.reset()
      }
    })
  }

  public adminService: AdminService

  private restartQuiz(): void {
    this.store.clearQuizData()
    const lang = this.store.$lang()
    const firstPage = this.quizService.getEngine().getFirstPageInFlow()
    void this.router.navigate([lang, quizParentSlug, firstPage].filter(_isNotNullish))
  }

  public displayMenu(): void {
    void this.modalService.show({
      component: AdminMenuModalComponent,
      modalName: 'RedDotMenu',
      skipControls: true,
      api: {
        select: async (action: AdminMenuAction) => {
          void this.modalService.remove()
          switch (action.type) {
            case 'skip': {
              this.bypassQuiz(action.payload!.goal)
              break
            }
            case 'skipToSection': {
              this.skipToSection(action.payload!.goal, action.payload!.sectionIndex)
              break
            }
            case 'restart': {
              this.restartQuiz()
              break
            }
            case 'changeCountry': {
              this.overrideCountry(action.payload?.country, action.payload?.region)
              break
            }
            case 'clearCountry': {
              this.clearCountryOverride()
              break
            }
            case 'setExperiment': {
              this.setExperiment(action.payload?.experiment, action.payload?.bucket)
              break
            }
            case 'clearExperiments': {
              this.clearExperiments()
              break
            }
            case 'logOutUser': {
              await this.logOutUser()
              break
            }
            case 'triggerPaymentWithNonce': {
              this.orderService.redDotNonce$.next(action.payload!)
              break
            }
            case 'timeTravel': {
              this.timeTravel(action.payload!.time)
              break
            }
            case 'resetTime': {
              this.timeTravel(undefined)
              break
            }
            case 'toggleInspector': {
              this.toggleInspector()
              break
            }
          }
        },
      },
    })
  }

  clearCountryOverride(): void {
    localStorage.removeItem('e2eCountry')
    localStorage.removeItem('e2eRegion')
    cookieService.setCookie('firebase-country-override', '', -1)
  }

  overrideCountry(country?: string, region?: string): void {
    if (!country) return
    localStorage.setItem('e2eCountry', country)
    cookieService.setCookie('firebase-country-override', country, 3)

    if (region) localStorage.setItem('e2eRegion', region.toLowerCase())
    else localStorage.removeItem('e2eRegion')

    location.reload()
  }

  private experimentPrefix = 'ABBA_'

  setExperiment(experimentNr?: string, bucket?: string): void {
    if (!experimentNr || !bucket) return
    localStorage.removeItem('removeAllAbTest')
    localStorage.setItem(`${this.experimentPrefix}${experimentNr}`, bucket)
  }

  clearExperiments(): void {
    const experimentKeys = Object.keys(localStorage).filter(k =>
      k.startsWith(this.experimentPrefix),
    )
    experimentKeys.forEach(k => localStorage.removeItem(k))
    localStorage.setItem('removeAllAbTest', 'true')
  }

  async logOutUser(): Promise<void> {
    await this.sessionService.logout()
    sessionStorage.clear()
  }

  // undefined resets time
  timeTravel(overrideTime: IsoDateTime | undefined): void {
    this.store.$reddot.update(s => ({
      ...s,
      overrideTime,
    }))
  }

  sequenceIndex = 0

  public async touchStartEventListener(e: TouchEvent): Promise<void> {
    const clientX = e.touches[0]?.clientX

    if (!clientX) {
      this.sequenceIndex = 0
      return
    }

    const sequence: ((clientX: number) => boolean)[] = [
      this.left,
      this.right,
      this.left,
      this.right,
      this.left,
      this.right,
    ]

    if (sequence[this.sequenceIndex]!(clientX)) {
      this.sequenceIndex++
    } else {
      this.sequenceIndex = 0
      return
    }

    if (this.sequenceIndex === sequence.length) {
      await this.adminService.toggleRedDot()
      this.sequenceIndex = 0
    }
  }

  private right(clientX: number): boolean {
    return clientX > window.innerWidth - 40
  }

  private left(clientX: number): boolean {
    return clientX < 40
  }

  private getFlowDefinition(goal: GoalBreakpointOption): FlowDefinition {
    return {
      [Breakpoint.goal]: goal,
      [Breakpoint.birthControlMethod]: BirthControlMethodBreakpointOption.pill,
      [Breakpoint.sideEffects]: SideEffectsBreakpointOption.skip,
      [Breakpoint.hormonalBirthControlHowLong]:
        HormonalBirthControlHowLongBreakpointOption.fewMonths,
      [Breakpoint.medicalConditions]: MedicalConditionsBreakpointOption.skip,
      [Breakpoint.cycleChanges]: CycleChangesBreakpointOption.skip,
      [Breakpoint.whichWearable]: [WhichWearableBreakpointOption.skip],
    }
  }

  private getQuizDataInput(goal: GoalBreakpointOption): QuizDataInputWithABTests {
    // Basic information about the user
    const basic: QuizDataInput = {
      name: 'Amalia',
      ageRange: AgeRangeOption.thirties,
      fertilityGoal: goal,
    }
    // Common quiz data for all long-quiz goals
    const common: QuizDataInputWithABTests = {
      ...basic,
      whichWearable: [],
      birthControlMethod: BirthControlMethodBreakpointOption.none,
      avgCycleLength: AvgCycleLengthOption.twentyOneToTwentyEight,
      lastPeriodDate: localDate.today().minus(28, 'day').toISODate(),
      medicalConditions: [],
      periodTrackerUsage: TrueOrFalseOption.false,
      consideringPurchase: ConsideringPurchaseOption.yesAppleWatch,
      periodRegularity: PeriodRegularityOption.canUsuallyPredict,
      cycleChanges: [],
      hormonalBirthControlUsage: HormonalBirthControlUsageBreakpointOption.no,
      sleep: SleepOption.sleepThroughTheNight,
      sleepConsistency: SleepConsistencyOption.regularHours,
      nightSleep: NightSleepBreakpointOption.sleepsThroughTheNight,
      feelingStressed: FeelingStressedBreakpointOption.noStress,
      sleepWithTracker: SleepWithTrackerOption.yesWithRing,
      wearWatchToBed: WearWatchToBedOption.no,
      deviceTrait: DeviceTraitOption.premium,
      statementDescription: StatementDescriptionOption.routinesAreImportant,
    }

    switch (goal) {
      case GoalBreakpointOption.plan: {
        return {
          ...common,
          hormonalBirthControlUsage: HormonalBirthControlUsageBreakpointOption.lastMonth,
          hormonalBirthControlHowLong: HormonalBirthControlHowLongBreakpointOption.fewMonths,
          conceiveTiming: ConceiveTimingBreakpointOption.justStarted,
          bestTimeInCycle: BestTimeInCycleOption.afterPeriod,
          knowingYourSexDrive: KnowingYourSexDriveOption.veryWell,
          sexFrequency: SexFrequencyOption.fewTimesWeek,
          knowPregnantDays: KnowPregnantDaysOption.notSure,
          discussedFertility: DiscussedFertilityOption.yesIHave,
          partnerInvolvement: PartnerInvolvementOption.veryInvolved,
        }
      }

      case GoalBreakpointOption.trackPregnancy: {
        return { ...basic, fertilityGoal: GoalBreakpointOption.trackPregnancy }
      }

      case GoalBreakpointOption.postPartum: {
        return { ...basic, fertilityGoal: GoalBreakpointOption.postPartum }
      }

      default: {
        return {
          ...common,
          fertilityGoal: GoalBreakpointOption.prevent,
          birthControlMethod: BirthControlMethodBreakpointOption.pill,
          sideEffects: [],
        }
      }
    }
  }

  /**
   * Toggles the inspector on the page.
   * This shows a red outline around all images and all translated text (assuming they've been set up)
   * On hover, a tooltip in the top left corner will show the translation key or the image src.
   */
  private toggleInspector(): void {
    let sub: Subscription | undefined

    const translations = (this.translateService.translations[this.translateService.currentLang] ||
      {}) as StringMap

    enum NotificationType {
      Console = 0,
      Alert = 1,
    }

    const searchForTextAndNotify = (text: string, type: NotificationType): void => {
      const match = getMatchingTranslationKey(text || '', translations)
      if (text && match) {
        const fn = type === NotificationType.Console ? console.log : alert
        fn(`Found translation for text:\n${text}\n\n${match.replace(/,/g, '')}`)
      }
    }

    /**
     * A handy tool in case we don't catch the text we want to inspect
     * The user can highlight text and we'll log the translation key
     * This is because elements like this:
     * ```html
     * <span>
     *   <img src="..." />
     *   Some text // Raw text, textContent of the span
     * </span>
     * ```
     * will only highlight the image, not the text
     */
    const selectListener = (): void => {
      const text = window.getSelection()?.toString().trim()
      if (text) {
        searchForTextAndNotify(text, NotificationType.Alert)
        window.getSelection()?.removeAllRanges()
      }
    }
    ;(window as any).getKey = (text: string): void =>
      searchForTextAndNotify(text, NotificationType.Console)

    // Toggle class and turn off the inspecter if we toggle it off
    const body = document.querySelector('body')
    if (body) {
      const activated = body.classList.toggle('inspector')
      if (!activated) {
        sub?.unsubscribe()
        document.removeEventListener('pointerup', selectListener)
        ;(window as any).getKey = undefined
        return
      }
    }

    // The dom can update freely, so we re-run the inspector on every stable event
    sub = this.ngZone.onStable.subscribe(() => {
      recInspect(document.body, translations)
    })

    document.addEventListener('pointerup', selectListener)

    // if we route, we toggle the inspector off. No need to unsubscribe in a singleton service
    this.router.events.subscribe(e => {
      if (e instanceof NavigationStart) {
        body?.classList.remove('inspector')
        sub?.unsubscribe()
        sub = undefined
      }
    })
  }

  private bypassQuiz(goal: GoalBreakpointOption): void {
    this.store.clearQuizData()
    logger.log('Bypassing quiz with goal', goal)
    const dataInput = this.getQuizDataInput(goal)
    const flow = getFlowForDefinition(this.getFlowDefinition(goal), this.quizService, dataInput)
    this.store.updateQuizData({
      ...dataInput,
    })
    this.store.$quiz.update(s => ({
      ...s,
      flow,
    }))

    this.store.$account.update(s => ({
      ...s,
      goal: goalBreakpointOptionToGoal[goal],
    }))

    const { exit } = this.store.$quiz()
    const lang = this.store.$lang()

    if (!exit) {
      console.error('Exit should be defined for current quiz')
      return
    }

    void this.router.navigate([lang, ...exit])
  }

  private skipToSection(goal: GoalBreakpointOption, sectionIndex: number): void {
    const { flow } = this.store.$quiz()
    const currentPage = _lastOrUndefined(location.pathname.split('/')) as Page | undefined

    if (!flow || !currentPage) {
      console.error('Flow, sections or currentPage is not defined for bypassSection')
      return
    }

    const dataInput = this.getQuizDataInput(goal)
    const flowDefinition = this.getFlowDefinition(goal)
    const newFlow = getFlowForDefinition(flowDefinition, this.quizService, dataInput)

    const sectionToSkipto = newFlow[sectionIndex]
    if (!sectionToSkipto) {
      console.error('No next section found for bypassSection')
      return
    }

    const nextPage = sectionToSkipto[0]
    if (!nextPage) {
      if (shortQuizGoalOptions.includes(goal)) {
        this.bypassQuiz(goal)
      } else {
        console.error('No next page found for bypassSection')
      }
      return
    }

    this.store.$quiz.update(s => ({
      ...s,
      flow: newFlow,
      data: {
        ...dataInput,
        ...s.data,
      },
    }))

    this.store.$account.update(s => ({
      ...s,
      goal: goalBreakpointOptionToGoal[goal],
    }))

    const lang = this.store.$lang()
    void this.router.navigate([lang, ParentPage.quiz, nextPage])
  }
}
